import React, { useEffect, useState } from 'react';
import { Redirect, RouteComponentProps, useMatch } from '@reach/router';
import {
  ContentContainer,
  LayoutContainer,
} from '../../utils/style/extendable';
import { useSelector } from 'react-redux';
import { appDispatch, RootState } from '../../store/store';
import ContentSidebar from '../Profile/ContentSidebar';
import GeneralSearch from '../Tasks/GeneralSearch';
import {
  ICompany,
  ICompanyProviders,
  TProjects,
} from '../../store/companies/companiesTypes';
import {
  IProvider,
  IProviderItemViewWithData,
} from '../../store/providers/providerTypes';
import TaskSidebar from '../Tasks/TaskSidebar';
import {
  isCompanyAdmin,
  isCompanySupport,
} from '../../store/companies/companiesUtils';
import { changeStateOFSideBar } from '../../store/app/appSlice';
import {
  getCompanyManagers,
  requireDocument,
  requireDocumentInFolder,
  requireProvider,
} from '../../store/providers/providerSlice';
import { addGuideToList } from '../../store/siteGuide/SiteGuideSlice';
import { isOutPatientDomain, outpatientLoginRedirect } from '../../api/Utils';
import {
  checkProviderStatus,
  IUserFlag,
} from '../../store/thisUser/thisUserSlice';
import { getRedirectToLoginLocation } from '../../utils/happyFunctions';
import { display } from '../../components/ErrorView/ErrorDialog';
import { getCompanyHandler } from '../../store/companies/companiesSlice';
import { requireSeveralUser } from '../../store/users/usersSlice';
import { OpenItemsContext } from '../../store/context/OpenItemsContext';
import { IOpenItem } from '../../store/entity/IOpenItem';
import ItemsViewer from '../../components/FileManager/ItemsViewer/Survey/ItemsViewer';
import { getItemUrl } from '../Profile/Panels/DocumentManager/ItemView';
import OpenItem from '../../components/FileManager/DataClass/OpenItem';
import { StepType, useTour } from '@reactour/tour';
import i18n from '../../i18n';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import { addTourStep } from '../../context/Tour/TourWrapper';

const mainLayoutStyle: React.CSSProperties = {
  minHeight: '100dvh',
};

const subLayoutStyle: React.CSSProperties = {
  flexDirection: 'row',
};

const contentLayoutStyle: React.CSSProperties = {
  overflow: 'hidden',
};

interface IProps {
  children: React.ReactNode;
}

type ISurveyTabs =
  | 'edit'
  | 'settings'
  | 'identification'
  | 'chart'
  | 'analyse'
  | 'versions'
  | 'view'
  | 'responses'
  | 'patientsForm';

type IComponentProps = RouteComponentProps & IProps;

const IndexRouter: React.FC<IComponentProps> = ({ children }) => {
  const thisUser = useSelector((state: RootState) => state.thisUser);

  const welcomeTour = !thisUser?.userId
    ? []
    : [
        {
          selector: '.welcome',
          content: `${i18n.t('welcome', { ns: 'general' })}`,
          position: 'center' as StepType['position'],
          category: 'welcome',
          style: {
            zIndex: '99999999999999',
          },
        },
      ];

  const tourFlags = useSelector(
    (state: RootState) => state.thisUser.userFlags['tour'] || []
  );
  const { setSteps } = useTour();

  const [openItems, setOpenItems] = useState<IOpenItem[]>([]);
  const [openItemId, setOpenItemId] = useState<null | string>();
  const [hasInitialized, setHasInitialized] = useState(false);

  const isMobile = useMobileDetection();
  const isSideBarCollapsed = useSelector(
    (state: RootState) => state.app.UI.sidebar.isSideBarCollapsed
  );

  const isContent = useMatch('/content/:providerId/*');
  const isTask = useMatch('/t/:companyId/*');

  const [isActiveGeneralSearch, setIsActiveGeneralSearch] =
    useState<boolean>(false);

  const providers = useSelector((state: RootState) => state.providers);
  const userLoggedIn = thisUser && thisUser.isLoggedIn;

  const provider: {
    data: IProvider | null;
    [key: string]: any;
  } | null = useSelector(
    (state: RootState) => state.providers.map?.[Number(isContent?.providerId)]
  );

  const companyId: null | number | undefined = isContent
    ? provider?.data?.managingCompanyId
    : isTask
      ? Number(isTask?.companyId)
      : null;

  const company: null | ICompany = useSelector(
    (state: RootState) => state.companies.data?.[companyId!] || null
  );

  const memberUserIds = Object.values(company?.members ?? {}).map(
    (it) => it.userId
  );
  const isSupport = isCompanySupport(thisUser, company);
  const isAdmin = isCompanyAdmin(thisUser, company);
  const isSelfProvider =
    provider && provider.data?.user === Number(thisUser.userId);

  const projects = company?.projects;
  const isSysAdmin =
    // @ts-expect-error it's ok
    (thisUser && thisUser.permissions?.includes('ModifyProvider')) || isSupport;

  const handleChangeGeneralSearch = (status: boolean = false) => {
    setIsActiveGeneralSearch(status);
  };

  const handleCollapseSidebar = () => {
    return appDispatch(
      changeStateOFSideBar({ isCollapsed: !isSideBarCollapsed })
    );
  };

  const handleGetCompanyManagers = async (companyId: number) => {
    return await appDispatch(getCompanyManagers({ companyId }));
  };

  const handleGetDocumentsInFolder = (folderId: number, providerId: number) => {
    return appDispatch(requireDocumentInFolder({ folderId, providerId }));
  };

  const handleMinimizeAllItems = () => {
    openItems.map((item) => {
      if (openItemId === item.item.data.id.toString()) {
        setOpenItemId(null);
      }
    });
  };

  useEffect(() => {
    if (!hasInitialized && isMobile) {
      appDispatch(changeStateOFSideBar({ isCollapsed: true }));
      setHasInitialized(true);
    }
  }, [isMobile, hasInitialized]);

  useEffect(() => {
    const handleEnableGeneralSearch = (event: any) => {
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }
      const userAgentData = (navigator as any).userAgentData;
      const isMac = (userAgentData?.platform || navigator.platform)
        .toUpperCase()
        .includes('MAC');

      if (event.key === '/') {
        setIsActiveGeneralSearch(true);
      }
      if (isMac && event.metaKey && event.key.toLowerCase() === 'k') {
        setIsActiveGeneralSearch(true);
      } else if (
        !isMac &&
        event.shiftKey &&
        event.ctrlKey &&
        event.key.toLowerCase() === 'k'
      ) {
        setIsActiveGeneralSearch(true);
      }
    };

    window.addEventListener('keydown', handleEnableGeneralSearch);
    return () =>
      window.removeEventListener('keydown', handleEnableGeneralSearch);
  }, []);

  useEffect(() => {
    const providerId = isContent && Number(isContent.providerId);
    appDispatch(addGuideToList(['manageDocuments', 'search-content']));
    const isLoggedIn = thisUser ? thisUser.isLoggedIn : false;

    if (isOutPatientDomain() && !isLoggedIn) {
      window.location.href = outpatientLoginRedirect;
    }

    if (isLoggedIn) {
      appDispatch(checkProviderStatus({}));
    }

    // appDispatch(getAppInstance({}));
    const requests = [] as Promise<any>[];
    if (!provider && providerId) {
      requests.push(
        appDispatch(requireProvider({ providerId: providerId })).then(
          undefined,
          (err: any) => {
            if (err.code === 403)
              window.location.href = getRedirectToLoginLocation();
          }
        )
      );
    }

    Promise.all(requests)
      .then(() => {
        if (providerId) {
          let companyId;
          const thisProvider = providers.map[providerId!];
          if (thisProvider) {
            companyId = thisProvider.data?.managingCompanyId;
            if (companyId && !company) {
              appDispatch(
                getCompanyHandler({
                  companyId,
                  templateStub: null,
                  companyStub: false,
                  projectStub: false,
                })
              );
            }
          }
        }
      })
      .catch((err) => {
        if (err.code === 403)
          window.location.href = getRedirectToLoginLocation();
        display(err);
      });
  }, []);

  useEffect(() => {
    if (isContent && isContent.providerId) {
      const providerId = isContent && Number(isContent.providerId);
      const thisProvider = providers.map?.[providerId!];
      if (thisProvider) {
        const companyId = thisProvider.data?.managingCompanyId;
        if (companyId && !company) {
          appDispatch(
            getCompanyHandler({
              companyId,
              templateStub: null,
              companyStub: false,
              projectStub: false,
            })
          );
        }
      }
    }
  }, [providers.map]);

  useEffect(() => {
    if (isTask) {
      const userId = Number(thisUser.userId);
      const inboxId = company?.members?.[userId]?.conversationInboxId;
      if (!memberUserIds || !inboxId || !companyId) return;

      (async () => {
        const promises = [];
        promises.push(
          appDispatch(requireSeveralUser({ usersList: memberUserIds }))
        );
        Promise.allSettled(promises).catch((err: any) => {
          display(err);
        });
      })();
    }
  }, [memberUserIds.length]);

  useEffect(() => {
    if (companyId) {
      const fetchCompanyData = async () => {
        await handleGetCompanyManagers(companyId);
      };
      fetchCompanyData();
    }
  }, [companyId]);

  useEffect(() => {
    const hasId = new URLSearchParams(window.location.search).has('id');
    if (!hasId && openItemId) {
      setOpenItemId(null);
    }
  }, [window.location.href]);

  useEffect(() => {
    if (
      !tourFlags.find((flag: IUserFlag) => flag.flagName === 'welcome') &&
      !window.location.href.includes('homepage') &&
      thisUser.userId
    ) {
      addTourStep(setSteps, welcomeTour, true);
      // setSteps?.((prevState) => [...(welcomeTour || []), ...(prevState || [])]);
    }
  }, [tourFlags]);

  const handleDeleteQueryParam = (id: number) => {
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);
    if (params.has('id') && params.get('id') === id.toString()) {
      currentUrl.search = '';
      window.history.replaceState(null, '', currentUrl.href);
    }
  };

  const handleReplaceOrPushQueryParam = (id: number) => {
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);
    params.set('id', id.toString());
    if (openItemId) {
      window.history.replaceState(
        { path: currentUrl.pathname + '?' + params.toString() },
        '',
        currentUrl.pathname + '?' + params.toString()
      );
    } else {
      window.history.pushState(
        { path: currentUrl.pathname + '?' + params.toString() },
        '',
        currentUrl.pathname + '?' + params.toString()
      );
    }
  };

  const handleCloseItem = (itemId: number) => {
    for (let i = 0; i < openItems.length; i++) {
      if (openItems[i].item.data.id === itemId) {
        setOpenItems((prevState) =>
          prevState.filter((item) => item.item.data.id !== itemId)
        );
        break;
      }
    }
    if (Number(openItemId) === itemId) {
      setOpenItemId(null);
      window.history.back();
    }
  };

  const handleOpenItem = (
    item: IProviderItemViewWithData,
    status: string,
    closeCurrent?: boolean,
    isNewTab?: boolean,
    params?: { [key: string]: any },
    mandatoryTagIds?: number[]
  ) => {
    addQueryParamToEmptyUrl(item.data.id, params);
    if (isNewTab) {
      const url = getItemUrl(item);
      window.open(url, '_blank');
      return;
    }
    for (let j = 0; j < openItems.length; j++) {
      const i = openItems[j];
      if (item.data.id === i.item.data.id) {
        i.status = status as ISurveyTabs;
        setOpenItemId(item.data.id.toString());
        return;
      }
      if (closeCurrent && Number(openItemId) === Number(i.item.data.id)) {
        openItems.splice(j, 1);
        j--;
      }
    }
    setOpenItems([
      ...openItems,
      new OpenItem(item, status, {
        mandatoryTagIds: mandatoryTagIds,
      }) as any as IOpenItem,
    ]);
    setOpenItemId(item.data.id.toString());
  };

  const handleReOpenItem = (itemId: number) => {
    for (const openItem of openItems) {
      if (openItem.item.data.id === itemId) {
        setOpenItemId(itemId.toString());
        break;
      }
    }
    handleReplaceOrPushQueryParam(itemId);
  };

  const addQueryParamToEmptyUrl = (
    id: number,
    paramsObjectList?: { [key: string]: string }
  ) => {
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);
    params.set('id', id.toString());
    if (Object.keys(paramsObjectList || {}).length) {
      Object.keys(paramsObjectList!).forEach((key) => {
        params.set(key, paramsObjectList![key]);
      });
    }
    window.history.pushState(
      { path: currentUrl.pathname + '?' + params.toString() },
      '',
      currentUrl.pathname + '?' + params.toString()
    );
  };

  const changeOpenItems = async (itemId: number, _: number) => {
    let item =
      providers.items?.[Number(isContent?.providerId)]?.items?.[itemId];
    if (!item) {
      await appDispatch(requireDocument({ documentId: itemId })).then(
        (result: any) => {
          item = { data: result.item };
        }
      );
    }
    if (item?.data?.type !== 'folder' && item?.data?.type !== 'searchTag') {
      const listOfOpenItemsId = openItems.map((el: any) => el.item.data.id);
      const newOpenItem = new OpenItem(item, 'responses');

      setOpenItemId(itemId.toString());
      setOpenItems(
        listOfOpenItemsId.includes(item.data.id)
          ? openItems
          : [...openItems, newOpenItem]
      );
      return {
        openItemId: itemId,
        openItems: listOfOpenItemsId.includes(item.data.id)
          ? openItems
          : [...openItems, newOpenItem],
      };
    } else {
      setOpenItemId(null);
      return null;
    }
  };

  const handleChangeState = (itemId: number, state: string) => {
    setOpenItems((prevState: any) => {
      return prevState.map((openItem: IOpenItem) => {
        if (openItem.item.data.id === itemId)
          return { ...openItem, status: state };
        else return openItem;
      });
    });
  };

  const handleMinimizeItem = (itemId: number) => {
    if (openItemId === itemId.toString()) {
      setOpenItemId(null);
      window.history.back();
    }
  };

  const openNextItem = (providerId: number) => {
    const currentItemId = openItemId;
    if (!currentItemId) return;
    const providerItems = providers.items[providerId];
    const currentItemOrder = providerItems.items[currentItemId].data.order;
    const currentItemDad = providerItems.items[currentItemId].data.dadId;
    let nextCondidateId: any = undefined;
    let nextCondidateOrder: any = undefined;
    let nextItem = undefined;
    Object.values(providerItems.items).forEach((item: any) => {
      const itemOrder = item.data.order;
      const itemId = item.data.id;
      if (
        item.data.dadId === currentItemDad &&
        (itemOrder > currentItemOrder ||
          (itemOrder === currentItemOrder && itemId > Number(currentItemId)))
      ) {
        if (
          !nextCondidateId ||
          nextCondidateOrder > itemOrder ||
          (nextCondidateId === itemOrder && nextCondidateId > itemId)
        ) {
          nextCondidateId = itemId;
          nextCondidateOrder = itemOrder;
          nextItem = item;
        }
      }
    });

    if (nextItem) {
      handleOpenItem(nextItem, 'responses', true);
    } else {
      handleCloseItem(Number(currentItemId));
    }
  };

  if (document.location.pathname === '/') {
    return <Redirect noThrow={true} to="homepage" />;
  }
  return (
    <LayoutContainer style={mainLayoutStyle}>
      {/* I have removed pointer-events */}
      <div
        className="welcome"
        style={{
          position: 'relative',
          top: '50%',
          left: '50%',
          width: '0.1rem',
          height: '0.1rem',
        }}
      ></div>
      <GeneralSearch
        companyId={Number(companyId)}
        isActive={isActiveGeneralSearch}
        onChangeGeneralSearchStatus={handleChangeGeneralSearch}
        providerId={isContent?.providerId}
      />
      <LayoutContainer
        style={userLoggedIn ? subLayoutStyle : undefined}
        dir={'row'}
      >
        {isTask || (userLoggedIn && company) ? (
          <TaskSidebar
            collapsed={isSideBarCollapsed}
            companyId={Number(companyId)}
            companyName={company?.name || ''}
            companyAdmin={isAdmin as boolean}
            hasCompany={!!companyId}
            hasPDP={!!company?.pdpPanels?.length}
            hasUnreadMessage={false}
            onCollapse={handleCollapseSidebar}
            projects={projects as TProjects}
            providers={company?.relatedProviders as ICompanyProviders}
            supportOrOwner={(isSupport || isSelfProvider) as boolean}
            allProviders={providers}
            getDocumentsInFolder={handleGetDocumentsInFolder}
            unreadChatMessage={false}
            onChangeGeneralSearchStatus={handleChangeGeneralSearch}
            company={company}
          />
        ) : provider?.data ? (
          <ContentSidebar
            // @ts-expect-error JSX error
            isCollapsed={isSideBarCollapsed}
            isSelfProvider={isSelfProvider}
            isAdmin={isSysAdmin}
            managingCompanyId={companyId}
            onCollapse={handleCollapseSidebar}
            provider={provider.data}
          />
        ) : null}
        <OpenItemsContext.Provider
          value={{
            openItems: openItems.reduce((acc: number[], currentValue) => {
              acc.push(currentValue.item.data.id);
              return acc;
            }, []),
            changeOpenItems: changeOpenItems,
            handleOpenItem: handleOpenItem,
            handleMinimizeAllItems: handleMinimizeAllItems,
          }}
        >
          <LayoutContainer style={contentLayoutStyle}>
            <ContentContainer>{children}</ContentContainer>
          </LayoutContainer>
          <ItemsViewer
            openId={openItemId as string | number}
            openItems={openItems}
            onClose={handleCloseItem}
            onMinimize={handleMinimizeItem}
            onReOpen={handleReOpenItem}
            changeState={handleChangeState}
            dispatch={appDispatch}
            openNextItem={openNextItem}
            onAddQueryparams={addQueryParamToEmptyUrl}
            onDeleteQueryparams={handleDeleteQueryParam}
            onReplaceQueryparams={handleReplaceOrPushQueryParam}
          />
        </OpenItemsContext.Provider>
      </LayoutContainer>
    </LayoutContainer>
  );
};

export default IndexRouter;
